import type { ReactNode } from "react";
import { Card } from "@heroui/react";
import { cn } from "@/lib/utils";

interface PanelProps {
	title?: ReactNode;
	description?: ReactNode;
	/** Right-aligned header slot: filters, segmented controls, buttons. */
	actions?: ReactNode;
	/** Remove body padding — for tables and charts that run edge to edge. */
	flush?: boolean;
	className?: string;
	bodyClassName?: string;
	children?: ReactNode;
}

/**
 * The one container every page is built from: a HeroUI Card with a dense,
 * terminal-style header (title left, actions right, hairline divider).
 */
export function Panel({ title, description, actions, flush, className, bodyClassName, children }: PanelProps) {
	const hasHeader = title || description || actions;
	return (
		<Card className={cn("gap-0 overflow-hidden border border-border p-0", className)}>
			{hasHeader && (
				<Card.Header className="flex min-h-11 flex-row flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-separator px-4 py-2.5">
					{/* On a narrow screen the actions wrap under the title instead of squeezing it. */}
					<div className="min-w-[min(12rem,100%)] flex-1">
						{title && <Card.Title className="truncate text-sm font-semibold">{title}</Card.Title>}
						{description && (
							<Card.Description className="truncate text-xs text-muted">{description}</Card.Description>
						)}
					</div>
					{actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
				</Card.Header>
			)}
			<Card.Content className={cn(flush ? "p-0" : "p-4", bodyClassName)}>{children}</Card.Content>
		</Card>
	);
}
